'use client';

import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { createContext, useContext, type ReactNode } from 'react';

type SearchSurface = {
  /**
   * Which results surface this is: a listing page (a category, popularity-
   * ranked) vs the search page. Seeded server-side by the page component.
   * Drives the default sort, which sort options are offered, and the
   * `popularity` context.
   *
   * Separate from SearchDefinitionContext (which carries the surface's engine
   * definition) because this is what the DISPLAY layer needs: the generated
   * hooks expose no way to read back the engine's configured searchHub, and
   * inferring the surface from the pathname coupled sort to the route.
   */
  isListing: boolean;
};

const SearchSurfaceContext = createContext<SearchSurface>({ isListing: false });

export const SearchSurfaceProvider = ({
  isListing,
  children,
}: SearchSurface & { children: ReactNode }) => (
  <SearchSurfaceContext.Provider value={{ isListing }}>
    {children}
  </SearchSurfaceContext.Provider>
);

export const useSearchSurface = () => useContext(SearchSurfaceContext);

/** The Coveo search hub backing the current surface. */
export const useSurfaceHub = (): CoveoSearchHub =>
  useSearchSurface().isListing ? CoveoSearchHub.LISTING : CoveoSearchHub.SEARCH;
